import { useState } from 'preact/hooks';
import {
  describeRefusal,
  enablePush,
  isIOS,
  isStandalone,
  pushState,
  type PushState,
} from '../lib/push.js';

const DISMISS_KEY = 'mess:push-prompt-dismissed';

/**
 * Приглашение включить уведомления.
 *
 * Отдельный блок, а не строчка в настройках, потому что без уведомлений
 * мессенджер бесполезен, а на iOS порядок действий неочевиден: сначала
 * установка на домашний экран, и только потом появляется сама возможность
 * запросить разрешение.
 */
export function PushPrompt() {
  const [state, setState] = useState<PushState>(() => pushState());
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === '1');

  if (state === 'granted' || state === 'unsupported') return null;
  if (dismissed && state !== 'needs-install') return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  if (state === 'needs-install') {
    return (
      <div class="section">
        <div class="notice">
          <strong>Чтобы приходили уведомления, добавь приложение на домашний экран.</strong>
          <br />
          На iPhone и iPad это единственный способ: в обычной вкладке Safari уведомления
          недоступны на уровне системы.
          <br />
          <br />
          Нажми «Поделиться» внизу Safari → «На экран „Домой“» → открой приложение с иконки
          и вернись сюда.
        </div>
      </div>
    );
  }

  if (state === 'denied') {
    return (
      <div class="section">
        <div class="notice">
          <strong>Уведомления запрещены в настройках браузера.</strong>
          <br />
          {/*
            Совет про замок в адресной строке верен только во вкладке браузера.
            В установленном приложении адресной строки нет, и разрешение живёт
            в системных настройках — подсказку выбирает describeRefusal.
          */}
          {isIOS() ? 'Открой «Настройки» → «Уведомления» → Mess и разреши их.' : describeRefusal('denied')}
          <br />
          <br />
          <button class="btn ghost" onClick={dismiss}>
            Понятно, скрыть
          </button>
        </div>
      </div>
    );
  }

  const turnOn = async () => {
    setBusy(true);
    setReason(null);
    try {
      const result = await enablePush();
      setState(result.state);
      // Молча вернуть ту же кнопку — худший из возможных ответов: нажатие
      // выглядит несработавшим, и причину искать негде.
      if (!result.ok) setReason(result.reason ?? 'Не получилось включить уведомления.');
    } catch (error) {
      console.error('не удалось подписаться на уведомления:', error);
      setState(pushState());
      setReason(error instanceof Error ? error.message : 'Не получилось включить уведомления.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="section">
      <div class="notice">
        <strong>Включи уведомления.</strong> Без них сообщения увидишь только когда сам
        откроешь приложение.
        {!isStandalone() && !isIOS() && (
          <>
            <br />
            Приложение можно установить — тогда оно откроется отдельным окном и будет
            работать без интернета.
          </>
        )}
      </div>
      {reason && <div class="notice warn">{reason}</div>}
      <div class="row">
        <button class="btn" onClick={turnOn} disabled={busy} style="flex:1">
          {busy ? 'Подключаю…' : 'Включить уведомления'}
        </button>
        <button class="btn ghost" onClick={dismiss}>
          Позже
        </button>
      </div>
    </div>
  );
}
